// OpenMeta — Dictionnaire FR
// Convention : namespaces par section ; interpolation via {count}, {name}, etc.

export const fr = {
  common: {
    email_placeholder: 'votre@email.fr',
    already_in: 'Vous êtes déjà sur la liste ! ✨',
    lang_short_fr: 'FR',
    lang_short_en: 'EN',
    switch_lang: 'Changer de langue',
    switch_theme_dark: 'Passer en mode sombre',
    switch_theme_light: 'Passer en mode clair',
    close: 'Fermer',
    open_menu: 'Ouvrir le menu',
  },
  errors: {
    generic: 'Une erreur est survenue, réessayez dans un instant.',
    rate_limit_generic: 'Trop de requêtes envoyées, revenez dans un moment.',
    rate_limit_generator: 'Vous avez atteint la limite de générations, revenez dans un moment.',
    rate_limit_login: 'Trop de tentatives de connexion. Merci de réessayer dans quelques minutes.',
    llm_unavailable: 'Le générateur est momentanément indisponible, réessayez dans un instant.',
    email_required: "Merci d'indiquer une adresse email.",
    email_invalid: 'Cette adresse email ne semble pas valide.',
    email_too_long: 'Cette adresse email est trop longue.',
    email_duplicate: 'Vous êtes déjà sur la liste !',
    generate_generic: 'Une erreur est survenue lors de la génération, réessayez dans un instant.',
    concept_not_found: "Ce concept n'existe plus. Générez-en un nouveau.",
    unlock_generic: 'Impossible de débloquer votre concept pour le moment.',
    unlock_rate_limit: 'Trop de tentatives, revenez dans un moment.',
    contact_generic: "Impossible d'envoyer votre message pour le moment, réessayez dans un instant.",
    contact_invalid_fields: 'Certaines informations semblent invalides.',
    checkout_generic: "Le paiement est momentanément indisponible, réessayez dans un instant.",
    checkout_invalid: 'Ce plan n\'est pas valide. Rechargez la page et réessayez.',
    prototype_generic: "Impossible de construire le prototype pour le moment.",
    prototype_locked: "Ce concept n'est pas encore débloqué.",
    prototype_not_found: "Ce concept n'existe pas ou plus.",
    prototype_rate_limit: "Trop de prototypes construits, revenez dans un moment.",
    prototype_unavailable: "La construction du prototype a échoué, réessayez dans un instant.",
    auth_bad_credentials: 'Email ou mot de passe incorrect.',
    auth_email_taken: 'Un compte existe déjà avec cette adresse email.',
    auth_invalid_input: 'Certaines informations semblent invalides.',
    auth_rate_limit: 'Trop de tentatives, revenez dans un moment.',
    auth_generic: "Un souci est survenu. Réessayez dans un instant.",
    auth_terms_required: "Vous devez accepter les Conditions d'utilisation et la Politique de confidentialité pour créer votre compte.",
  },
  navbar: {
    home: 'Accueil',
    applications: 'Applications',
    universe: 'Univers',
    universe_short: 'Univers',
    generator: 'Générateur',
    pricing: 'Tarifs',
    contact: 'Contact',
    cta_waitlist: 'Rejoindre la waitlist',
  },
  waitlist: {
    submit: 'Rejoindre la waitlist',
    submitting: 'Envoi…',
    submitted: 'Merci ! ✨',
    success: 'Bienvenue dans la waitlist OpenMeta ! ✨',
    already_in: 'Vous êtes déjà sur la liste ! ✨',
    no_spam: 'Aucune carte bancaire. Aucun spam.',
    no_spam_short: 'Gratuit — un email suffit, pas de spam.',
    counter_prefix: 'Déjà',
    counter_suffix_single: 'créateur nous a rejoint.',
    counter_suffix_plural: 'créateurs nous ont rejoint.',
    counter_chip_single: 'créateur sur la waitlist',
    counter_chip_plural: 'créateurs sur la waitlist',
  },
  footer: {
    tagline:
      'La plateforme No-Code + IA qui donne le pouvoir de créer aux experts métiers. Cinq univers, une infinité de possibles.',
    section_universe: 'Univers',
    section_product: 'Produit',
    section_info: 'Info',
    product_applications: 'Nos Applications',
    product_generator: 'Générateur',
    product_pricing: 'Tarifs',
    product_contact: 'Contact',
    info_legal: 'Mentions légales',
    info_privacy: 'Confidentialité',
    info_terms: "Conditions d'utilisation",
    copyright: '© {year} OpenMeta.tech — Tous droits réservés.',
    slogan: 'No Code. No Limits. Just AI.',
  },
  hero: {
    eyebrow: 'Plateforme No-Code + IA — 5 univers métier',
    title_l1: 'No Code.',
    title_l2: 'No Limits.',
    title_l3: 'Just AI.',
    subtitle:
      "La plateforme No-Code + IA qui donne le pouvoir de créer aux experts métiers — médecins, enseignants, artistes, linguistes et créateurs de contenu. Décrivez votre idée, l'IA assemble vos blocs.",
    universes_label: 'Les 5 univers OpenMeta',
    illustration_alt:
      "Composition d'application no-code assemblée en briques 3D translucides colorées",
  },
  home: {
    universes: {
      eyebrow: '5 univers, 5 langages métier',
      title_l1: 'Un écosystème modulaire',
      title_l2: 'pour chaque métier.',
      subtitle:
        "Chaque univers rassemble ses briques no-code, ses modèles d'IA spécialisés et ses écrans clés. Vous choisissez le vôtre, vous décrivez votre projet — l'IA fait le reste.",
    },
    applications_teaser: {
      eyebrow: 'Nos Applications',
      title_l1: 'Déjà en production.',
      title_l2: 'Fabriquées avec OpenMeta.',
      see_all: 'Voir toutes nos applications',
    },
    how: {
      eyebrow: 'Comment ça marche',
      title_l1: 'Trois étapes.',
      title_l2: 'Zéro ligne de code.',
      subtitle:
        "OpenMeta ne vous demande jamais de penser « application » ou « produit ». Vous parlez impact métier, l'IA fait la traduction.",
      steps: [
        {
          title: 'Décrivez votre idée',
          text: "En quelques phrases, expliquez le besoin métier que vous voulez adresser. Pas de langage technique : parlez comme à un collègue.",
        },
        {
          title: "L'IA assemble vos blocs",
          text: "OpenMeta choisit les briques no-code adaptées, imagine les écrans clés et compose une application cohérente et prête à personnaliser.",
        },
        {
          title: 'Publiez votre application',
          text: 'Ajustez les textes, les visuels, la couleur de votre univers, puis publiez en un clic. Vos utilisateurs sont déjà là.',
        },
      ],
    },
    testimonials: {
      eyebrow: 'Ils créent déjà avec OpenMeta',
      title_l1: 'Les experts métiers',
      title_l2: 'prennent la main.',
      quotes: [
        {
          name: 'Dr. Amélie Fournier',
          role: 'Médecin généraliste, Lyon',
          quote:
            "J'ai lancé un carnet de suivi vocal pour mes patients âgés en une soirée. Ils l'utilisent tous les jours — et j'ai enfin des données fiables entre deux consultations.",
        },
        {
          name: 'Claire Vasseur',
          role: 'Enseignante en collège, Nantes',
          quote:
            "Mes élèves progressent avec un coach de français IA que j'ai imaginé un dimanche soir. Sans OpenMeta, j'aurais mis six mois — et je serais encore en train d'écrire un cahier des charges.",
        },
        {
          name: 'Malik Sadaoui',
          role: 'Artiste plasticien, Marseille',
          quote:
            "Mon expo a un compagnon numérique qui raconte l'histoire de chaque œuvre. Les visiteurs restent trois fois plus longtemps. Et je n'ai jamais touché à une ligne de code.",
        },
      ],
    },
    waitlist_cta: {
      eyebrow: 'Early access',
      title_l1: "Vous n'attendrez pas",
      title_l2: 'la prochaine vague.',
      subtitle:
        "Rejoignez la liste des bâtisseurs qui recevront OpenMeta en avant-première. Un email — c'est tout ce qu'il faut.",
    },
  },
  verticals: {
    sante: {
      label: 'Santé & Bien-être',
      short: 'Santé',
      tagline: "Le soin, la prévention et le bien-être — augmentés par l'IA.",
      description:
        "Des applications sur mesure pour les soignants, les patients et les praticiens du bien-être : suivi, prévention, accompagnement au quotidien.",
      promise: 'Créez votre application santé en 30 minutes.',
      intro:
        "OpenMeta rassemble les briques et l'IA dont les soignants et les acteurs du bien-être ont besoin — sans dépendre d'une équipe technique.",
      use_cases: [
        { title: 'Assistant de suivi patient', text: "Enregistrez les constantes, symptômes et humeurs par la voix, générez un résumé lisible pour la prochaine consultation." },
        { title: 'Journal bien-être IA', text: 'Un compagnon quotidien : humeur, sommeil, exercices de respiration guidés, encouragements personnalisés.' },
        { title: 'Prise de rendez-vous intelligente', text: 'Un formulaire qui pose les bonnes questions, oriente vers le bon praticien et confirme automatiquement le créneau.' },
        { title: 'Protocole de suivi post-opératoire', text: 'Envoyez à vos patients les bonnes recommandations au bon moment, avec relances douces et alertes en cas de signal faible.' },
      ],
    },
    culture: {
      label: 'Culture & Art',
      short: 'Culture',
      tagline: "Faire vivre le patrimoine, révéler les artistes, créer de l'émotion.",
      description:
        "Des expériences immersives pour les musées, galeries, artistes et festivals : parcours interactifs, catalogues intelligents, publics engagés.",
      promise: 'Faites parler vos œuvres et vos lieux, en 30 minutes.',
      intro:
        "OpenMeta permet aux artistes, galeries et institutions culturelles de créer des expériences numériques immersives — sans une seule ligne de code.",
      use_cases: [
        { title: "Compagnon d'exposition", text: "Une visite guidée intelligente qui raconte l'histoire de chaque œuvre, adapte le récit à l'âge du visiteur et propose des jeux." },
        { title: 'Catalogue interactif', text: "Numérisez votre fonds, tissez des liens entre les œuvres et laissez vos publics explorer votre patrimoine." },
        { title: 'Billetterie inspirée', text: 'Un tunnel de réservation élégant, avec recommandations personnalisées et rappel avant la visite.' },
        { title: "Portfolio d'artiste vivant", text: "Présentez votre parcours, vos œuvres et laissez l'IA suggérer les meilleures pièces à chaque profil de collectionneur." },
      ],
    },
    formation: {
      label: 'Formation & Éducation',
      short: 'Formation',
      tagline: 'Transmettre, faire monter en compétences, certifier — sans friction.',
      description:
        "Des espaces d'apprentissage personnalisés pour formateurs, écoles et entreprises : parcours pédagogiques, coaching IA, certifications.",
      promise: 'Lancez votre parcours de formation en 30 minutes.',
      intro:
        "OpenMeta donne aux formateurs, écoles et responsables RH les outils pour concevoir des parcours vivants, personnalisés et efficaces.",
      use_cases: [
        { title: 'Micro-formations métier', text: 'Des modules courts, vidéo et textuels, avec quiz IA, pour former vos équipes à leur rythme sur les compétences clés.' },
        { title: 'Coach de compétences IA', text: "Un mentor virtuel qui répond aux questions des apprenants, corrige leurs exercices et signale les points à retravailler." },
        { title: 'Parcours certifiant', text: 'Suivez la progression de chaque apprenant, délivrez des certificats numériques et centralisez les preuves de compétences.' },
        { title: 'Onboarding nouveau collaborateur', text: "Un compagnon d'intégration qui déroule les bonnes ressources jour après jour et répond aux questions récurrentes." },
      ],
    },
    langues: {
      label: 'Apprentissage des Langues',
      short: 'Langues',
      tagline: "Apprendre une langue par la conversation, l'immersion et le jeu.",
      description:
        "Des expériences conversationnelles pour apprenants, écoles de langues et polyglottes : dialogues immersifs, prononciation IA, progression ludique.",
      promise: 'Créez votre coach de langue vivante en 20 minutes.',
      intro:
        "OpenMeta transforme l'apprentissage des langues en conversations, jeux et immersions — bien plus qu'une simple app de vocabulaire.",
      use_cases: [
        { title: 'Coach conversationnel', text: 'Un partenaire de conversation infatigable, qui adapte le vocabulaire et corrige la prononciation en douceur.' },
        { title: 'Immersion métier', text: 'Des mises en situation professionnelles : négocier en anglais, présenter un projet, mener un entretien.' },
        { title: 'Compagnon de voyage', text: "Les 200 phrases essentielles, l'aide en direct et une bibliothèque de contextes culturels pour chaque destination." },
        { title: 'Défi linguistique quotidien', text: 'Un mini-jeu par jour, ludique, gratifiant, qui ancre le vocabulaire sans y penser.' },
      ],
    },
    social: {
      label: 'Réseaux Sociaux IA',
      short: 'Social',
      tagline: 'Créer, publier, engager — au rythme des créateurs modernes.',
      description:
        "Des outils pour créateurs, community managers et marques : génération de contenu, planification, modération, mesure d'impact.",
      promise: 'Assemblez votre studio de contenu en 20 minutes.',
      intro:
        "OpenMeta arme les créateurs et les marques indépendantes pour publier, engager et mesurer — sans passer par une agence.",
      use_cases: [
        { title: 'Studio de contenu IA', text: 'Générez captions, hashtags et variantes, planifiez vos publications multi-plateformes et gardez votre ton de marque.' },
        { title: 'Community manager assisté', text: 'Répondez plus vite aux commentaires et messages, avec des suggestions calibrées sur votre voix.' },
        { title: "Analyse d'audience", text: "Comprenez qui vous suit, ce qui déclenche l'engagement et où concentrer vos prochains efforts." },
        { title: 'Newsletter enrichie', text: 'Transformez vos posts les plus performants en newsletter automatisée pour capter votre communauté hors plateformes.' },
      ],
    },
  },
  univers_page: {
    back_home: "← Retour à l'accueil",
    universe_badge_prefix: 'Univers',
    cta_generator: 'Essayer le générateur',
    cta_waitlist: 'Rejoindre la waitlist',
    section_use_cases_eyebrow: "Cas d'usage",
    section_use_cases_title: 'Ce que vous pouvez créer.',
    section_showcases_eyebrow: 'Exemples créés avec OpenMeta',
    section_showcases_title: "Ils ont déjà imaginé leur app.",
    section_showcases_subtitle:
      "Ces concepts ont été générés en quelques secondes par des utilisateurs comme vous, à partir d'une simple description.",
    showcases_empty: 'Les vitrines de cet univers arrivent très prochainement.',
    cta_generate_mine: 'Générer le mien',
    waitlist_eyebrow: 'Early access',
    waitlist_title_l1: 'Soyez parmi les premiers à créer dans',
  },
  applications_page: {
    eyebrow: 'Nos Applications',
    title_l1: 'Des applications réelles,',
    title_l2: 'pour de vrais métiers.',
    subtitle:
      "Voici quelques-unes des applications déjà construites dans l'écosystème OpenMeta — conçues par et pour des experts métiers, en production sur leurs propres domaines.",
    cta_eyebrow: "La prochaine, c'est la vôtre.",
    cta_title_l1: 'Prêt à imaginer',
    cta_title_l2: 'votre application ?',
    cta_subtitle: 'Rejoignez la waitlist — vous serez parmi les premiers à créer avec OpenMeta.',
    visit_app: "Visiter l'application",
  },
  applications_data: {
    'aria-radiology': {
      tagline: 'Analyse radiologique assistée par IA.',
      description:
        "Un espace praticien où l'IA aide à l'interprétation d'imagerie médicale, en soutien du radiologue. Un allié pour gagner en confiance et en rapidité sur les analyses complexes.",
    },
    medurgence: {
      tagline: "Assistant IA médical pour l'urgence.",
      description:
        "Protocoles pas-à-pas pour arrêt cardiaque, AVC, choc ou état de mal, calculateur de doses selon le poids, vérification des interactions et chat IA d'aide à la décision, adossés aux référentiels Urg'Ara.",
    },
    medlib: {
      tagline: "Un doute sur votre santé ? Sachez quoi faire et qui appeler.",
      description:
        "Assistant d'orientation santé grand public : en quelques questions guidées, il vous indique le bon niveau de recours — 15, urgences, médecin sous 24 h, médecin traitant ou pharmacien. Douze fiches premiers secours gratuites, consultables hors-ligne. Sans diagnostic ni posologie — ne remplace pas un avis médical.",
    },
    atlantys: {
      tagline: 'Réseau social des agents IA.',
      description:
        "Une plateforme où des centaines d'agents IA — ministres, conseillers thématiques — publient, débattent et proposent des politiques publiques sur les grands sujets contemporains, en lien avec Sciences Po & IA.",
    },
    'urgence-pratique': {
      tagline: "Copilot clinique IA pour l'urgence et le SMUR.",
      description:
        "Assistant intelligent réservé aux professionnels de santé : réponses structurées et sourcées selon les recommandations SFMU, SFAR, SRLF et HAS, Prescrire AI, assistant vocal temps réel, protocoles décisionnels, calculateurs de scores et de doses, fiches médicaments et suivi patients anonymisé.",
    },
    medsynapse: {
      tagline: 'Le réseau social médical propulsé par 850 agents IA experts.',
      description:
        "51 communautés médicales, discussions cliniques ouvertes et avis collégial d'un staff médical de 250 spécialistes — une plateforme gratuite pour partager, apprendre et confronter les pratiques.",
    },
    'lexia-school': {
      tagline: 'Apprendre les langues avec votre coach IA vocale.',
      description:
        "Une méthode en 9 étapes — mot, sens, prononciation, image mentale, phrase, contexte, écoute, production, révision — orchestrée par Lexia, coach vocale qui écoute, corrige et vous fait parler dès la première minute.",
    },
  },
  pricing: {
    eyebrow: 'Tarifs',
    title_l1: 'Un prix juste,',
    title_l2: 'pour chaque étape.',
    subtitle:
      "Choisissez Basique pour lancer votre première app, Pro pour orchestrer jusqu'à 10 apps sans limite d'IA, ou Entreprise pour un accompagnement dédié.",
    toggle_monthly: 'Mensuel',
    toggle_annual: 'Annuel',
    discount: '-20%',
    popular: 'Le plus choisi',
    price_month_suffix: '/mois',
    price_year_suffix: '/an',
    price_on_quote: 'Sur devis',
    faq_eyebrow: 'FAQ',
    faq_title_l1: 'Vos questions,',
    faq_title_l2: 'nos réponses.',
    waitlist_eyebrow: 'Early access',
    waitlist_title: "Bloquez votre tarif d'ouverture.",
    waitlist_subtitle:
      "Les inscrits en waitlist bénéficieront d'une remise de bienvenue exclusive à l'ouverture.",
    checkout_loading: 'Redirection vers le paiement…',
    checkout_error: "Le paiement est momentanément indisponible, réessayez dans un instant.",
    checkout_cancel_toast: "Paiement annulé — vous pouvez reprendre quand vous voulez.",
    checkout_cancel_banner_title: 'Paiement annulé.',
    checkout_cancel_banner_subtitle: 'Aucun débit — reprenez quand vous voulez.',
    checkout_cancel_banner_close: 'Fermer',
    tiers: [
      {
        key: 'basique',
        name: 'Basique',
        tag: '29€/mois',
        tagAnnual: '279€/an',
        description: 'Pour lancer sereinement votre première application IA.',
        features: [
          '1 application publiée',
          'Générateur de concepts IA inclus',
          'Blocs no-code essentiels des 5 verticales',
          'Sous-domaine openmeta.tech',
          'Support par email',
        ],
        cta: "S'abonner",
        checkoutPlan: 'basique',
      },
      {
        key: 'pro',
        name: 'Pro',
        tag: '290€/mois',
        tagAnnual: '2784€/an',
        description: "Pour orchestrer plusieurs apps et débloquer tout l'arsenal IA d'OpenMeta.",
        features: [
          "Jusqu'à 10 applications",
          "Tous les blocs IA des 5 verticales",
          "Générations de concepts illimitées",
          'Domaine personnalisé',
          "Support prioritaire (< 24 h)",
          "Statistiques d'usage détaillées",
        ],
        cta: "Passer au Pro",
        checkoutPlan: 'pro',
      },
      {
        key: 'entreprise',
        name: 'Entreprise / Agence',
        tag: 'Sur devis',
        tagAnnual: 'Sur devis',
        description: "Pour les agences et organisations qui déploient OpenMeta à grande échelle.",
        features: [
          'Applications illimitées',
          'Accompagnement humain dédié',
          'SSO & gestion des rôles',
          'Déploiement dédié (cloud FR)',
          'SLA 99,9 %',
          'RGPD & audit sécurité',
        ],
        cta: 'Nous contacter',
      },
    ],
    faq: [
      {
        q: 'Puis-je changer de plan à tout moment ?',
        a: "Oui — vous pouvez passer de Basique à Pro, ou l'inverse, quand vous le souhaitez. Le prorata est automatiquement calculé par notre système de facturation.",
      },
      {
        q: 'Puis-je annuler à tout moment ?',
        a: "Oui. Aucun engagement — vous pouvez arrêter votre abonnement en un clic depuis votre espace ; vous conservez l'accès jusqu'à la fin de la période payée.",
      },
      {
        q: 'Quelle est la différence entre Basique et Pro ?',
        a: 'Basique vous permet de publier 1 application avec les blocs essentiels. Pro débloque jusqu\'à 10 applications, tous les blocs IA des 5 verticales et un support prioritaire.',
      },
      {
        q: 'Puis-je essayer avant de payer ?',
        a: "Bien sûr : le générateur de concepts est accessible librement sur openmeta.tech/generateur. Vous voyez concrètement ce que produit l'IA avant de vous abonner.",
      },
      {
        q: 'Où sont hébergées mes données ?',
        a: 'En France, chez un hébergeur certifié. Nous ne revendons jamais vos données et respectons scrupuleusement le RGPD.',
      },
      {
        q: "Faites-vous des tarifs pour les associations et l'éducation ?",
        a: 'Oui, contactez-nous : nous avons une offre dédiée pour les associations, écoles et projets de recherche.',
      },
    ],
  },
  contact: {
    eyebrow: 'Contact',
    title_l1: 'Parlons de',
    title_l2: 'votre projet.',
    subtitle:
      'Démo, partenariat, article de presse ou simple question — nous vous répondons sous 48h ouvrées.',
    email_label_short: 'Email direct',
    name_label: 'Nom',
    name_placeholder: 'Votre nom',
    email_label: 'Email',
    subject_label: 'Sujet',
    subject_placeholder: 'Choisir un sujet',
    subjects: {
      demo: 'Démo',
      partenariat: 'Partenariat',
      presse: 'Presse',
      autre: 'Autre',
    },
    message_label: 'Message',
    message_placeholder: 'Parlez-nous de votre projet, votre besoin, votre timing…',
    message_hint: 'Entre 10 et 2000 caractères.',
    submit: 'Envoyer le message',
    submitting: 'Envoi…',
    success_title: 'Message envoyé.',
    success_subtitle: 'Merci ! Nous revenons vers vous très vite.',
    success_toast: 'Merci ! Votre message est parti — nous revenons vers vous très vite.',
    error_generic_toast: 'Merci de corriger les champs indiqués.',
    validation: {
      name_required: "Merci d'indiquer votre nom.",
      name_too_long: 'Ce nom est trop long.',
      email_required: "Merci d'indiquer une adresse email.",
      email_invalid: 'Cette adresse email ne semble pas valide.',
      subject_required: 'Merci de choisir un sujet.',
      message_min: 'Votre message doit contenir au moins 10 caractères.',
      message_max: 'Votre message ne doit pas dépasser 2000 caractères.',
    },
  },
  generator: {
    eyebrow: 'Le Générateur',
    title_l1: 'Décrivez votre idée.',
    title_l2: "L'IA imagine l'application.",
    subtitle:
      "En quelques secondes, obtenez un concept d'application complet — modules IA, écrans clés et blocs no-code prêts à assembler.",
    seo_description:
      "Décrivez votre idée d'application en français. L'IA d'OpenMeta imagine votre concept no-code — nom, modules, écrans clés, blocs à assembler — en quelques secondes.",
    label_universe: 'Choisissez votre univers',
    label_description: "Décrivez votre idée d'application",
    desc_hint: 'Entre {min} et {max} caractères. Parlez de votre métier, pas de technique.',
    avg_time: 'Temps moyen : 10 secondes • 100% en français',
    submit: 'Générer mon concept',
    placeholders: {
      sante: "Ex : Une app pour aider les kinés à suivre les exercices de leurs patients à distance, avec rappels et vidéos.",
      culture: "Ex : Un guide interactif pour ma galerie d'art qui raconte l'histoire de chaque œuvre exposée.",
      formation: "Ex : Une micro-formation vidéo pour les commerçants qui veulent créer leur boutique en ligne.",
      langues: "Ex : Un compagnon d'apprentissage de l'espagnol pour préparer un voyage à Séville en 3 semaines.",
      social: "Ex : Un studio de contenu qui génère mes captions et hashtags pour Instagram et TikTok chaque semaine.",
    },
    loading_steps: [
      'Analyse de votre idée…',
      'Choix des blocs no-code adaptés…',
      'Assemblage des modules IA…',
      'Rédaction du concept…',
    ],
    loading_title: 'Votre concept se dessine.',
    loading_avg: 'Cela prend en moyenne 10 secondes.',
    gate_title: 'Débloquez votre concept complet',
    gate_subtitle: 'Modules IA, écrans clés, blocs no-code et cibles — un email suffit.',
    gate_submit: 'Débloquer le concept',
    gate_submitting: 'Déblocage…',
    gate_no_spam: "Aucun spam — un simple heads-up quand OpenMeta ouvre.",
    unlocked_badge: 'Concept débloqué',
    section_users: 'Pour qui ?',
    section_modules: 'Modules ({count})',
    section_screens: 'Écrans clés ({count})',
    section_blocks: 'Blocs no-code à assembler',
    estimate_prefix: 'Estimation OpenMeta :',
    estimate_suffix: 'pour construire cette app.',
    regenerate: 'Générer un autre concept',
    samples_eyebrow: 'Ils ont été créés ici',
    samples_title: 'Quelques concepts sortis du générateur.',
    samples_cta: 'Voir nos applications en production',
    unlock_success: 'Concept débloqué et waitlist rejointe !',
    unlock_already_in: 'Bon retour parmi nous ! Concept débloqué.',
    validation: {
      desc_min: 'Décrivez votre idée en au moins {min} caractères.',
      desc_max: 'Restez sous {max} caractères.',
      universe_required: 'Choisissez une verticale.',
    },
  },
  not_found: {
    badge: 'Erreur 404',
    title: '404',
    heading: "Cette page n'existe pas encore.",
    subtitle:
      "L'univers OpenMeta est modulaire — mais ce module-ci n'a pas encore été assemblé. Retournez à l'accueil, ou laissez-vous guider par le générateur.",
    cta_home: "Retour à l'accueil",
    cta_generator: 'Essayer le générateur',
    seo_title: 'Page introuvable (404)',
    seo_description: "Cette page n'existe pas encore. Revenez à l'accueil OpenMeta.",
  },
  placeholder: {
    eyebrow: 'Bientôt disponible',
    back_home: "Retour à l'accueil",
    no_spam: 'Prévenus en premier — sans spam.',
    pages: {
      mentions: {
        title: 'Mentions légales',
        subtitle:
          "Éditeur, hébergeur et informations légales seront publiés au lancement d'OpenMeta.",
      },
      privacy: {
        title: 'Confidentialité',
        subtitle:
          'Notre politique de confidentialité sera publiée au lancement. En attendant, vos données de waitlist ne sont utilisées que pour vous prévenir.',
      },
    },
  },
  medlib_qr: {
    banner_text: 'votre santé à portée de main',
    dismiss_aria: "Fermer l'annonce MedLib",
  },

  seo: {
    home_title: 'OpenMeta — No Code. No Limits. Just AI.',
    home_description:
      "OpenMeta est la plateforme No-Code + IA qui donne le pouvoir de créer aux experts métiers : santé, culture, formation, langues et réseaux sociaux. Décrivez votre idée, l'IA assemble vos blocs.",
    applications_title: 'Nos Applications',
    applications_description:
      "Des applications réelles, en production : ARIA Radiology, MedUrgence, Atlantys, MedSynapse, Lexia School. Fabriquées par et pour des experts métiers avec OpenMeta.",
    pricing_title: 'Tarifs',
    pricing_description:
      "Grille tarifaire OpenMeta : Basique à 29€/mois, Pro à 290€/mois pour 10 applications, Entreprise/Agence sur devis. Mensuel/annuel avec -20% et FAQ complète.",
    contact_title: 'Contact',
    contact_description:
      "Parlons de votre projet — démo, partenariat, presse ou question simple. Nous répondons sous 48h ouvrées.",
    generator_title: 'Le Générateur',
    prototype_title: 'Prototype — OpenMeta',
    prototype_description: "Découvrez la maquette interactive d'une application imaginée par l'IA OpenMeta, écran par écran, dans un vrai cadre téléphone.",
    register_title: 'Créer un compte — OpenMeta',
    register_description: "Créez votre compte créateur OpenMeta pour préparer vos applications no-code.",
    login_title: 'Se connecter — OpenMeta',
    login_description: 'Accédez à votre espace créateur OpenMeta.',
    studio_title: 'Studio — OpenMeta',
    studio_description: 'Votre espace créateur OpenMeta : plan actif, quota et vos futures applications.',
  },
  auth: {
    register_eyebrow: 'Rejoindre OpenMeta',
    register_title: 'Créez votre compte créateur.',
    register_subtitle: 'Un compte pour piloter vos concepts, prototypes et bientôt vos applications no-code.',
    register_cta: 'Créer mon compte',
    login_eyebrow: 'Bon retour',
    login_title: 'Connectez-vous.',
    login_subtitle: 'Accédez à votre studio et vos applications.',
    login_cta: 'Se connecter',
    email_label: 'Email',
    password_label: 'Mot de passe',
    password_placeholder: 'Choisissez un mot de passe',
    password_hint: 'Minimum 8 caractères.',
    submitting: 'Un instant…',
    have_account: 'Déjà un compte ?',
    no_account: 'Pas encore de compte ?',
    register_success: 'Bienvenue ! Votre compte est prêt.',
    login_success: 'Ravi de vous revoir.',
    logout: 'Se déconnecter',
    my_apps: 'Mes applications',
    account_menu_title: 'Mon compte',
    errors: {
      password_required: 'Choisissez un mot de passe.',
      password_too_short: 'Minimum 8 caractères.',
      fill_both: 'Merci de renseigner email et mot de passe.',
      terms_required: "Vous devez accepter les Conditions d'utilisation et la Politique de confidentialité pour continuer.",
    },
    terms_prefix: "J'accepte les",
    terms_and: 'et la',
    terms_label_terms: "Conditions d'utilisation",
    terms_label_privacy: 'Politique de confidentialité',
  },
  legal: {
    eyebrow: 'Documents légaux',
    last_updated: 'Dernière mise à jour',
    toc_label: 'Sommaire',
    contact_title: 'Contact',
    courtesy_note: "Version française fait foi. Cette traduction anglaise est fournie à titre indicatif — en cas de divergence, la version française prévaut.",
  },
  studio: {
    eyebrow: 'Studio',
    title: 'Bonjour, {email}',
    subtitle: "Votre espace créateur. Créez vos applications, publiez-les sur openmeta.tech/app/…, gérez vos soumissions.",
    plan_label: 'Votre plan',
    plan_none: 'Aucun',
    plan_basique: 'Basique',
    plan_pro: 'Pro',
    plan_illimite: 'Accès Fondateur',
    unlimited_apps: 'Applications illimitées',
    unlimited_hint: "Publiez autant d'applications que vous voulez, sans limite de quota.",
    interval_month: 'Mensuel',
    interval_year: 'Annuel',
    quota_label: 'Applications publiées',
    apps_singular: 'application',
    apps_plural: 'applications',
    my_apps: 'Mes applications',
    create_app: 'Créer une application',
    empty_title: "Aucune application pour l'instant.",
    empty_subtitle: "Créez votre première mini-app en 5 minutes, ou partez d'un concept généré par l'IA.",
    explore_generator: 'Ouvrir le générateur',
    noplan_title: "Choisissez votre plan pour démarrer.",
    noplan_subtitle: "Vous n'avez pas encore de plan actif. Sélectionnez Basique ou Pro pour créer et publier vos applications.",
    noplan_cta: 'Voir les tarifs',
    needs_plan: 'Choisissez un plan pour créer votre première app.',
    status_published: 'Publiée',
    status_draft: 'Brouillon',
    edit: 'Éditer',
    publish: 'Publier',
    unpublish: 'Dépublier',
    delete: 'Supprimer',
    open_public: 'Ouvrir la page publique',
    published_toast: 'Application publiée ! Elle est en ligne.',
    unpublished_toast: 'Application dépubliée.',
    deleted_toast: 'Application supprimée.',
    delete_failed: 'Suppression impossible.',
    delete_confirm_title: 'Supprimer cette application ?',
    delete_confirm_body: 'La page publique et toutes les soumissions seront supprimées définitivement.',
    blocks: 'blocs',
    submissions: 'soumissions',
    chat_messages: 'messages',
    activity_7d: 'Activité 7 jours',
    visits: 'visites',
    moderation_title: 'Modération',
    moderation_no_reason: 'Cette application a été mise en pause. Contactez le support pour la débloquer.',
    moderation_blocked: 'Publication bloquée par la modération',
  },
  builder: {
    title: 'Constructeur',
    description: "Éditez votre application, prévisualisez-la puis publiez.",
    back_studio: 'Retour au studio',
    settings: 'Réglages',
    name_label: "Nom de l'application",
    slug_label: 'Slug (URL publique)',
    vertical_label: 'Verticale',
    lang_label: 'Langue',
    description_label: 'Description (SEO)',
    tabs: {
      build: 'Constructeur',
      preview: 'Aperçu',
      submissions: 'Soumissions',
    },
    blocks: {
      content: 'Page contenu',
      chatbot: 'Chatbot IA',
      form: 'Formulaire',
    },
    add_block: 'Ajouter un bloc :',
    blocks_empty: "Aucun bloc. Ajoutez une page contenu, un chatbot ou un formulaire pour commencer.",
    save: 'Sauvegarder',
    saving: 'Sauvegarde…',
    saved: 'Brouillon sauvegardé.',
    save_before_publish: 'Sauvegardez avant de publier.',
    published: 'Application publiée ! 🚀',
    unpublished: 'Application dépubliée.',
    preview_note: "Aperçu du rendu public. Les blocs interactifs (chatbot, formulaire) sont désactivés dans l'aperçu — publiez pour les tester en direct.",
    concept_panel_title: 'Plan de votre concept',
    concept_modules: 'Modules',
    concept_screens: 'Écrans clés',
    tips_title: 'Bonnes pratiques',
    tips: {
      slug: '• Le slug est votre URL publique et ne peut pas contenir d\'espaces.',
      persona: '• La persona du chatbot influence directement son ton et ses garde-fous.',
      publish: "• Basique = 1 app publiée, Pro = 10 apps. Les brouillons sont illimités.",
    },
    total_submissions: 'soumissions au total',
    refresh: 'Rafraîchir',
    export_csv: 'Export CSV',
    export_ok: 'CSV téléchargé.',
    export_failed: "Impossible d'exporter pour le moment.",
    no_submissions_yet: 'Aucune soumission pour le moment.',
    errors: {
      create_failed: "Impossible de créer l'application.",
      save_failed: 'Sauvegarde impossible pour le moment.',
      publish_failed: 'Publication impossible pour le moment.',
      not_found: "Cette application n'existe plus.",
      invalid: 'Certaines informations semblent invalides.',
      quota: 'Quota de publications atteint. Passez au plan supérieur.',
      max_blocks: 'Maximum 8 blocs par application.',
      slug_too_short: 'Le slug doit faire au moins 3 caractères.',
      slug_too_long: 'Le slug est trop long (40 caractères max).',
      slug_format: 'Le slug doit être en kebab-case (a-z, 0-9, tirets).',
      slug_reserved: 'Ce slug est réservé, choisissez-en un autre.',
      slug_taken: 'Ce slug est déjà pris, choisissez-en un autre.',
    },
    open_in_builder: 'Ouvrir dans le constructeur',
    open_in_builder_needs_login: 'Connectez-vous pour ouvrir dans le constructeur',
    open_in_builder_needs_plan: 'Passez à Basique ou Pro pour ouvrir dans le constructeur',
    opening: 'Ouverture…',
  },
  prototype: {
    build_cta: 'Construire le prototype',
    build_cta_loading: 'Construction en cours…',
    build_cta_view: 'Voir le prototype',
    loading_eyebrow: 'Prototype en construction',
    loading_title: 'On assemble votre application.',
    loading_steps: [
      'Analyse du concept…',
      'Assemblage des écrans…',
      'Application de votre thème…',
      "Injection des données d'exemple…",
      'Peaufinage des détails…',
    ],
    teaser_eyebrow: 'Bientôt',
    teaser_title: 'Publiez cette application en réel avec OpenMeta.',
    teaser_subtitle:
      "Le constructeur no-code arrive : reprenez ce prototype, personnalisez-le et mettez-le en ligne. Choisissez déjà votre formule pour être prêt à ouvrir dès le lancement.",
    teaser_cta: "Voir les tarifs",
    disclaimer:
      "Ce prototype est une maquette visuelle : les contenus sont générés à titre d'exemple, aucun traitement de données réel n'est effectué.",
    locked_title: 'Concept encore verrouillé.',
    locked_subtitle: "Débloquez ce concept depuis le générateur pour construire son prototype.",
    locked_cta: 'Ouvrir le générateur',
    notfound_title: 'Prototype introuvable.',
    notfound_subtitle: "Ce concept n'existe pas ou plus. Générez-en un nouveau depuis le générateur.",
    notfound_cta: 'Ouvrir le générateur',
    error_title: 'Petit contretemps.',
    error_subtitle: "La construction du prototype a échoué. Réessayez dans un instant.",
  },
  checkout_success: {
    seo_description: "Confirmation de votre abonnement OpenMeta et prochaines étapes.",
    polling_eyebrow: 'Confirmation',
    polling_title: 'On confirme votre paiement…',
    polling_subtitle: 'Encore quelques secondes, on récupère la confirmation auprès de Stripe.',
    success_eyebrow: 'Paiement confirmé',
    success_title: 'Bienvenue, votre abonnement est actif.',
    success_subtitle: "Vous allez recevoir un email de confirmation avec votre facture. Vous pouvez dès maintenant explorer le générateur de concepts et préparer votre première application.",
    plan_label: 'Plan',
    interval_month: 'Mensuel',
    interval_year: 'Annuel',
    confirmation_sent_to: 'Reçu envoyé à',
    cta_home: "Retour à l'accueil",
    cta_generator: 'Ouvrir le générateur',
    pending_eyebrow: 'En attente',
    pending_title: 'Le paiement est encore en cours de traitement.',
    pending_subtitle: "Aucun débit tant que le paiement n'est pas confirmé. Vous pouvez fermer cette page et revenir plus tard, ou reprendre depuis la page Tarifs.",
    cta_back_pricing: 'Retour aux tarifs',
    notfound_title: 'Session introuvable',
    notfound_subtitle: "Cette session de paiement n'est plus valide ou n'a jamais existé. Reprenez depuis la page Tarifs.",
    error_title: 'Petit contretemps',
    error_subtitle: "On a du mal à confirmer votre paiement en temps réel. Vérifiez votre boîte email dans quelques minutes — la facture Stripe fait foi.",
  },
};
